import { Card } from "./ui/card";
import { Trophy, Share2 } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return ""; }
};

/** Achievement badges earned by the profile owner. Currently only "challenge_complete"
 *  from Group Coaching, but the structure is future-proof for more badge kinds.
 */
export default function BadgesCard({ badges, displayName }) {
  if (!badges || badges.length === 0) return null;

  const share = (b) => {
    const summary = `🏆 ${displayName || "I"} completed the "${b.challenge_target || b.challenge_body || 'weekly challenge'}" challenge in ${b.group_name}.`;
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: "Challenge Complete", text: summary, url }).catch(() => {});
    } else {
      navigator.clipboard.writeText(`${summary} ${url}`).then(() => toast.success("Badge link copied to clipboard"));
    }
  };

  return (
    <Card className="mt-6 border border-slate-200 rounded-xl bg-white p-6" data-testid="profile-badges-card">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="w-4 h-4 text-amber-600" />
        <div className="text-[11px] uppercase tracking-widest font-semibold text-amber-700">
          Achievements
        </div>
        <div className="font-mono-num text-xs text-slate-500 ml-1">
          {badges.length} earned
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="profile-badges-grid">
        {badges.map((b) => (
          <div
            key={b.id}
            className="relative border border-amber-200 bg-gradient-to-br from-amber-50 to-white rounded-lg p-4 flex items-start gap-3 transition-shadow hover:shadow-md"
            data-testid={`profile-badge-${b.id}`}
          >
            <div
              className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 text-white"
              style={{ background: "linear-gradient(135deg,#FCD34D 0%,#D97706 100%)" }}
              aria-hidden
            >
              <Trophy className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-amber-700 mb-0.5">
                Challenge · {b.group_name || "Group"}
              </div>
              <div className="font-display font-bold text-sm text-slate-900 line-clamp-2">
                {b.challenge_target || b.challenge_body || "Weekly challenge"}
              </div>
              <div className="text-[11px] text-slate-500 mt-1 font-mono-num">
                {fmtDate(b.awarded_at)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => share(b)}
              className="absolute top-2 right-2 text-slate-400 hover:text-amber-700"
              data-testid={`profile-badge-share-${b.id}`}
              title="Share badge"
              aria-label="Share badge"
            >
              <Share2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}
